import React from 'react';
import { Sparkles } from 'lucide-react';

const CaseStudiesHero = () => {
  return (
    <section
      data-testid="case-studies-hero"
      className="relative pt-28 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: text + stats */}
          <div className="space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              Real results, real clients
            </div>

            <h1
              data-testid="case-studies-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight"
            >
              Case <span className="text-blue-600">Studies</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-xl">
              A closer look at how we turned growth challenges into measurable
              outcomes — across app marketing, advocacy campaigns and
              performance-led acquisition for global brands.
            </p>

            {/* Top-line stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
              {[
                { value: '1M+', label: 'Total downloads' },
                { value: '+50%', label: 'Install growth' },
                { value: '2.2M', label: 'TikTok impressions' },
                { value: '<$1', label: 'Cost per install' },
              ].map((stat, i) => (
                <div
                  key={i}
                  data-testid={`hero-stat-${i}`}
                  className="bg-white/80 backdrop-blur-sm border border-blue-100 rounded-2xl p-4 shadow-sm"
                >
                  <div className="text-xl sm:text-2xl font-bold text-blue-600 leading-none">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-600 mt-1.5 leading-snug">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: hero image */}
          <div className="relative">
            <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl hover:shadow-3xl transition-shadow duration-300 aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&auto=format&fit=crop&q=80"
                alt="Marketing analytics dashboards showing client case study results"
                className="w-full h-full object-cover"
                loading="eager"
              />
              {/* Soft brand overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/20 via-transparent to-transparent pointer-events-none" />
            </div>

            {/* Decorative blobs */}
            <div className="absolute -top-4 -right-4 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob pointer-events-none" />
            <div className="absolute -bottom-8 -left-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000 pointer-events-none" />

            {/* Floating badge */}
            <div className="absolute -bottom-6 left-6 lg:-left-6 z-20 bg-white border border-blue-100 rounded-2xl shadow-xl px-5 py-3 flex items-center gap-3">
              <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
              <div>
                <div className="text-xs text-gray-500 leading-none">
                  Active campaigns
                </div>
                <div className="text-sm font-bold text-gray-900 mt-1 leading-none">
                  5 client portfolios
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesHero;
